import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import { APIGetEventById } from '../../api';
import EventMediaPanel from '../../home_media_ui/EventMediaPanel';

const stateLabels = { new: 'Новое', planned: 'Запланировано', in_progress: 'В работе',
  drawing: 'Черчение', completed: 'Завершено', cancelled: 'Отменено', moved: 'Перенесено' };
const dateLabel = value => {
  if (!value) return null;
  const parts = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Chat membership does not grant event access. The ordinary event-detail API
// checks the role, assignment and region again each time this dialog opens.
export default function ChatEventPreview({ open, eventId, token, onClose }) {
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const [mediaViewing, setMediaViewing] = useState(false);
  const scope = `${eventId}:${token}`;
  useEffect(() => {
    let cancelled = false;
    setResult(null); setMediaViewing(false);
    if (open && eventId && token) {
      APIGetEventById(eventId, token).then(event => {
        if (!cancelled) setResult({ scope, event: event && String(event.id) === String(eventId) ? event : null });
      }).catch(() => { if (!cancelled) setResult({ scope, event: null }); });
    }
    return () => { cancelled = true; };
  }, [open, eventId, token, retry]);
  const resolved = result?.scope === scope;
  const event = resolved ? result.event : null;
  const deal = event?.bitrix_deal;
  const address = deal?.full_address || [deal?.city?.name, deal?.address].filter(Boolean).join(', ');
  const time = event?.time_event ? String(event.time_event).slice(0, 5)
    : event?.time_from && event?.time_to
      ? `${String(event.time_from).slice(0, 5)}–${String(event.time_to).slice(0, 5)}` : null;

  return <Modal open={!!open} onClose={onClose} disableEnforceFocus={mediaViewing}
    sx={{ zIndex: 10210, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, sm: 3 } }}
    slotProps={{ backdrop: { sx: { backdropFilter: 'blur(5px)' } } }}>
    <ModalDialog aria-label={`Событие #${eventId}`} sx={{ position: 'relative', inset: 'auto', transform: 'none',
      m: 0, p: 0, width: 720, maxWidth: '100%', borderRadius: '20px', overflow: 'hidden',
      maxHeight: 'calc(100dvh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <EventRoundedIcon sx={{ color: 'primary.500', fontSize: 21 }} />
          <Typography level="title-md">Событие #{eventId}</Typography>
          {event?.state && <Chip size="sm" variant="soft">{event.name_state || stateLabels[event.state] || event.state}</Chip>}
        </Stack>
        <IconButton aria-label="Закрыть событие" size="sm" variant="plain" onClick={onClose}><CloseRoundedIcon /></IconButton>
      </Stack>
      <Divider />
      <Box sx={{ minHeight: 0, overflowY: 'auto', px: { xs: 1.25, sm: 2 }, py: 2 }}>
        {!resolved ? <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ py: 4 }}>
          <CircularProgress size="sm" /><Typography level="body-sm">Загрузка события…</Typography>
        </Stack> : !event ? <Stack spacing={1.5}>
          <Alert color="warning">Событие недоступно. Возможно, изменились права доступа или подключение к серверу.</Alert>
          <Button variant="soft" onClick={() => setRetry(value => value + 1)}>Повторить</Button>
        </Stack> : <Stack spacing={2}>
          <Box>
            {address && <Stack direction="row" spacing={0.75} alignItems="flex-start">
              <PlaceRoundedIcon sx={{ fontSize: 20, color: 'primary.500' }} />
              <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>{address}</Typography>
            </Stack>}
            <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
              {dateLabel(event.date_event) && <Chip size="sm" variant="outlined">{dateLabel(event.date_event)}</Chip>}
              {time && <Chip size="sm" variant="outlined">{time}</Chip>}
              {deal?.square != null && <Chip size="sm" variant="soft">{deal.square} м²</Chip>}
              {event.service?.title && <Chip size="sm" variant="soft">{event.service.title}</Chip>}
            </Stack>
          </Box>
          <EventMediaPanel eventId={eventId} token={token} onViewerChange={setMediaViewing} />
        </Stack>}
      </Box>
    </ModalDialog>
  </Modal>;
}
